import { i18n, StringMap, TOptions } from 'i18next';
import React, { useEffect, useRef } from 'react';
import { TransProps as BaseTransProps, Trans, useTranslation } from 'react-i18next';

import { useLocale, useWorkbenchLocale } from '@tanium/react-locale-context';

import { DurationProps } from './formatters/relativeTimeFormatters';
import { getI18nInstance, ResourceObject } from './i18nFactory';

// TFunction type that claims to always return a string, which instances with fallbackLng do.
export type TFunction = (key: string, options?: TOptions<StringMap> | string) => string;

/**
 * Dynamic values used in translation strings must be of a restricted number of
 * types.
 */
export type DynamicValue = string | number | Date | string[] | number[] | Date[] | DurationProps;

/**
 * The `TransWrapper` will allow arbitrary properties whose values are
 * dynamically inserted into the translation. This prevents the need for the use
 * of the `values` property to pass in dynamic values in the translated string.
 */
export type DynamicValues = Record<string, DynamicValue>;

type AllowedTransProps<K extends keyof BaseTransProps<string>> = Pick<BaseTransProps<string>, K>;

interface DisallowedTransProps {
  /**
   * Do not use `values`. Instead, provide the key/value pairs as props to
   * `Trans` directly.
   *
   * @example
   * Old: <Trans values={{ foo: 'bar' }} />
   * New: <Trans foo="bar" />
   */
  values?: never;
  /**
   * Do not use `t`. Instead, import "Trans" from a localization.ts
   * file, and it will have t built-in because of how our custom TransWrapper works.
   */
  t?: never;
}

/**
 * Generates prop types for a `Trans` wrapper component
 */
export type TransWrapperPropsFactory<
  AllowedBasePropNames extends Exclude<keyof BaseTransProps<string>, keyof DisallowedTransProps>
> =
  | AllowedTransProps<AllowedBasePropNames>
  | (DynamicValues &
      // Redeclaring the allowed props here prevents TypeScript from mixing the allowed prop types with `DynamicValue`.
      AllowedTransProps<AllowedBasePropNames> &
      DisallowedTransProps);

/**
 * The `TransWrapper` can forward some allowed properties, as well as accept
 * arbitrary values to use when filling in dynamic values.
 */
export type TransWrapperProps = TransWrapperPropsFactory<
  'children' | 'count' | 'components' | 'i18nKey'
>;

/**
 * The signature of the `TransWrapper` component, which is both the functional
 * component and a `displayName` to make diagnostics easier.
 */
export type TransWrapperType = ((props: TransWrapperProps) => JSX.Element) & {
  displayName: string;
};

export const generateTranslationTools = (packageName: string, resources: ResourceObject) => {
  // This state is stored here, outside a react component, the first time an @tanium package is used.
  // That way, we have 'global space per package', so i18n can create/init only once per package.
  const i18nInstance: i18n = getI18nInstance(packageName, resources);
  const translatedLocales = Object.keys(resources);

  const useTranslationTools = () => {
    const workbenchLocale = useWorkbenchLocale();
    const hasMounted = useRef(false);
    // useTranslation has a useEffect which sets a listener on languageChanged to refresh the t function
    // So this line must appear before the useEffect below, since useEffects execute in line order.
    // eslint-disable-next-line @tanium/i18next-parser/translation-namespaces-are-string-literals
    const tools = useTranslation(packageName, { i18n: i18nInstance });

    // This ref tracks what language the tools were generated with. It will
    // differ from `i18nInstance.language` if another instance of
    // `useTranslationTools` has called `changeLanguage` prior to this instance
    // of `useTranslation` setting up event listeners for `languageChanged`.
    const toolsLanguageRef = useRef(i18nInstance.language);

    useEffect(() => {
      // The first hook to notice a locale change will update the instance for
      // the package. Doing so will trigger a rerender of other
      // `useTranslationTools` instances which have already attached listeners.
      if (i18nInstance.language !== workbenchLocale) {
        i18nInstance.changeLanguage(workbenchLocale).catch((err) =>
          // eslint-disable-next-line no-console
          console.error(
            `Failed to change i18n language to ${workbenchLocale} in ${packageName}: `,
            err,
          ),
        );
      } else if (!hasMounted.current && toolsLanguageRef.current !== workbenchLocale) {
        // On the initial render of the application, the `tools` are generated
        // with the fallback language since the instance has not had its
        // language changed to the workbench locale yet and instances of
        // `useTranslationTools` after the first will not yet have listeners
        // setup for `languageChanged` when `changeLanguage` is called by the
        // first instance. Directly emitting `languageChanged` will result in
        // `tools` being regenerated for the correct language.

        // Specifically, this handles loading the application with a workbench
        // locale other than the fallback language. Without this, the initial
        // `tools` are generated for the fallback language and would not update
        // unless the workbench locale updates.
        i18nInstance.emit('languageChanged');
      }
    }, [workbenchLocale]);

    // Referencing `i18nInstance.language` in the below hook results in a
    // false-positive on the `react-hooks/exhaustive-deps` lint rule because
    // `i18nInstance` is an "outer scope value" which won't rerender the hook.
    // This is a false-positive because we aren't expecting `language` to
    // trigger a rerender; we only want to track what value the `i18nInstance`
    // had when it was passed to `useTranslation`. Destructuring `language`
    // works around this without disabling the rule to avoid true-positive lint
    // errors from being accidentally ignored.
    const { language } = i18nInstance;
    // This needs to run after the main language handling effect so that
    // `toolsLanguageRef` refers to the language with which `tools` was
    // generated even if another instance of `useTranslationTools` has called
    // `changeLanguage`.
    useEffect(() => {
      toolsLanguageRef.current = language;
    }, [language]);

    useEffect(() => {
      hasMounted.current = true;
    }, []);

    // In case there is something wrong with the architectural approach here, let's find out quickly.
    if (!i18nInstance.isInitialized) {
      throw new Error('i18n instance not initialized (translation is not working)');
    }

    // Pretend t always returns a string. Which it is type-guaranteed to do, because the i18nInstance has a fallbackLng.
    return {
      /**
       * See available data formatters (with examples):
       * https://git.corp.tanium.com/tanium/coreui/blob/main/packages/react-i18n/src/formatters/interpolationFormatter.ts
       */
      t: tools.t as TFunction,
      ready: tools.ready,
    };
  };

  const TransWrapper: TransWrapperType = ({
    children,
    components,
    count,
    i18nKey,
    ...nonCountValues
  }) => {
    useTranslationTools();

    /*
     * Assemble props in a way where we avoid passing any props undefined.
     * Otherwise get errors with values=undefined where it unexpectedly fires the formatters.
     */
    const props = { i18n: i18nInstance } as BaseTransProps<string>;
    if (count !== undefined) props.count = count;
    if (i18nKey) props.i18nKey = i18nKey;
    if (components) props.components = components;
    const hasValues = Object.keys(nonCountValues).length;
    /*
     * The `Trans` component immediately re-spreads the values internally, so
     * any form of memoization here would be immediately defeated. If that
     * implementation changes, we can insert a hash-based memoization strategy
     * here. For now, though, it would be unnecessary overhead.
     */
    if (count !== undefined && hasValues) {
      props.values = { ...nonCountValues, count };
    } else if (hasValues) {
      props.values = nonCountValues;
    }

    return <Trans {...props}>{children}</Trans>;
  };
  TransWrapper.displayName = 'TransWrapper';

  const useCalculatedWorkbenchLocale = () => {
    const rawPreferredLocale = useLocale();
    const available = translatedLocales.includes(rawPreferredLocale as string);
    return (available ? rawPreferredLocale : 'en-US') as string;
  };

  return {
    Trans: TransWrapper,

    useTranslation: useTranslationTools,

    useCalculatedWorkbenchLocale,
  };
};
